import React from 'react';

import { SingleSelectOption } from '@/modules/common';

import { BaseOnboardingEntity } from '../../types/base-onboarding-entity';

export enum IntegrationConnectionStatus {
  Connected = 'connected',
  NotFound = 'notFound',
}

export interface SelectOption {
  label: string;
  value: string;
  customContent?: React.ReactNode;
}

/**
 * Defines an attribute row in the resolve-duplicates comparison table.
 * Each entity provides its own rows (e.g. name, email for team; name, client for projects).
 */
export interface OnboardingResolveConflictAttributeRow<TEntity extends BaseOnboardingEntity> {
  /** Form field name (used as the key in the form and Zod schema) */
  key: string;
  /** Display label for the row header */
  label: string;
  /** Build the dropdown options from the selected items */
  getSelectOptions: (items: TEntity[]) => SelectOption[];
  /** Get the display value for a given item's cell */
  getDisplayValue: (item: TEntity) => string;
  /** Check if a cell value should be highlighted as destructive (empty/missing) */
  isEmpty?: (item: TEntity) => boolean;
  /** Placeholder text for the select dropdown */
  placeholder: string;
  /** Whether this field is required in the form */
  required?: boolean;
  /** Custom Zod validation (if not provided, uses required string min(1) or optional string) */
  zodSchema?: import('zod').ZodTypeAny;
  /** Whether the resulting entity select is disabled */
  selectDisabled?: boolean;
  /** Tooltip shown when the select is disabled */
  selectDisabledTooltip?: string;
  /** Forced default value that overrides common-value auto-detection on modal open */
  forcedDefaultValue?: string;
  /**
   * When true, empty cells in person columns render in muted color instead of destructive red.
   * Use for optional fields where missing value is expected (e.g. email).
   */
  showMutedWhenEmpty?: boolean;
  /**
   * Optional override for computing the default form value from selected items.
   * When provided, takes priority over common-value detection and forcedDefaultValue.
   */
  resolveDefaultValue?: (items: TEntity[], commonValue: string) => string;
}

/**
 * Configuration for building the merge request from form data.
 */
export interface MergeRequestBuilder<TEntity extends BaseOnboardingEntity> {
  /**
   * Build the merge API request from form data + selected items + resolved integrations.
   * @param formData - The form values (attribute keys + integration keys)
   * @param selectedItems - The original selected entities
   * @param integrationsToUse - Parsed integrations from the form
   */
  build: (
    formData: Record<string, string>,
    selectedItems: TEntity[],
    integrationsToUse: { integration: string; sourceId: number }[]
  ) => unknown;
}

/**
 * Row data for the onboarding conflicts DataTable.
 * Each row represents either an attribute field or an integration field.
 */
export interface OnboardingConflictRow {
  /** Unique key for the row (form field name) */
  fieldKey: string;
  /** Display label for the attribute column */
  fieldLabel: string;
  /** Whether this row represents an integration field */
  isIntegrationRow?: boolean;
  /** Select options for the resulting entity dropdown */
  selectOptions: SingleSelectOption[];
  /** Placeholder for the select dropdown */
  placeholder: string;
  /** Display values for each person column, keyed by person index */
  personValues: OnboardingConflictPersonCell[];
  /** Whether the resulting entity select is disabled */
  selectDisabled?: boolean;
  /** Tooltip shown when the select is disabled */
  selectDisabledTooltip?: string;
}

export interface OnboardingConflictPersonCell {
  /** Display content for this person's cell */
  displayValue: React.ReactNode;
  /** Whether the value should be highlighted as destructive (empty/missing) */
  isDestructive: boolean;
  /** Whether the value should be rendered in muted color (e.g. "Not specified") */
  isMuted?: boolean;
}

/** Sentinel value used in the email select to represent "no email" / not specified */
export const NOT_SPECIFIED_OPTION_VALUE = '__not_specified__';

/** Category keys for grouping rows in the DataTable */
export const ONBOARDING_CONFLICT_CATEGORY_ATTRIBUTES = 'attributes';
export const ONBOARDING_CONFLICT_CATEGORY_INTEGRATIONS = 'integrations';
